import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  Info,
  Pencil,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { TableSkeleton, LinesSkeleton } from '@components/medicus-ui/Loaders';
import { EstadoLegajoBadge } from '@components/EstadoLegajoBadge';
import { ResolverObservacionModal } from '@components/ResolverObservacionModal';
import { CampoControl } from '@components/EditAsociadoModal';
import { FIELDS } from '@components/asociado-campos';
import { CAMPO_OPCIONES } from '@lib/catalogs';
import { validarFilaAsociado } from '@lib/validar-asociado';
import { buscarPersonas, type BusquedaMatchItem } from '@lib/services/busqueda.service';
import {
  getAltaManualPreflight,
  editarIntegrantesAltaManual,
  exportarAltaManual,
  darDeAltaManual,
  getAltasManualesEnCurso,
  type AltaManualPreflight,
  type AltaManualEnCursoItem,
  type IntegranteAltaManual,
  type ObservacionAltaManual,
} from '@lib/services/alta-manual.service';
import {
  resolverObservacionAltaManual,
  type ObservacionItem,
  type ResolverObservacionBody,
} from '@lib/services/observaciones.service';

const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
  '13': 'Adherente',
};

// La columna "estado fuerza" del Excel sale de DOS preguntas de la DDJJ del
// titular (titularidad + fuerza). Si el titular no tiene DDJJ, el operador las
// elige acá. Los `value` reproducen lo que espera el exporter
// (`derivarEstadoFuerza`): "En actividad" → "actividad", fuerza en minúscula.
const TITULARIDAD_OPCIONES = [
  { value: 'En actividad', label: 'En actividad' },
  { value: 'Retirado', label: 'Retirado' },
  { value: 'Pensionado', label: 'Pensionado' },
];
const FUERZA_OPCIONES = [
  { value: 'gna', label: 'GNA — Gendarmería' },
  { value: 'pna', label: 'PNA — Prefectura' },
  { value: 'iosfa', label: 'IOSFA' },
];

/** Extrae el mensaje de error del backend (soporta el shape `{error:{message}}`
 *  del filtro global y el `{message}` plano). */
function msgError(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { message?: string; error?: { message?: string } } } })
    ?.response?.data;
  return data?.error?.message ?? data?.message ?? fallback;
}

type Seleccion =
  | { tipo: 'legajo'; legajoId: string }
  | { tipo: 'borrador'; match: BusquedaMatchItem }
  | null;

export function AltaManualPage() {
  const [seleccion, setSeleccion] = useState<Seleccion>(null);

  function elegir(m: BusquedaMatchItem) {
    if (m.legajoId) setSeleccion({ tipo: 'legajo', legajoId: m.legajoId });
    else setSeleccion({ tipo: 'borrador', match: m });
  }

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Alta manual</h1>
        <p className="text-element-mid">
          Dar de alta a mano <strong>un legajo urgente</strong> (ej. paciente oncológica). Buscá la
          persona, validá que los datos pasen Binary, descargá el mismo Excel del alta masiva para ese
          legajo y, una vez cargado en Binary, marcalo como dado de alta. Si los datos no están
          correctos, <strong>no se habilita la descarga ni el pase a dado de alta</strong>.
        </p>
      </header>

      {!seleccion && (
        <>
          <Buscador onElegir={elegir} />
          <AltasEnCurso
            onAbrir={(legajoId) => setSeleccion({ tipo: 'legajo', legajoId })}
          />
        </>
      )}

      {seleccion?.tipo === 'borrador' && (
        <CartelBloqueo
          tono="error"
          titulo="No se puede dar de alta desde un padrón en borrador"
          onVolver={() => setSeleccion(null)}
        >
          <p style={{ margin: 0 }}>
            <strong>
              {seleccion.match.apellSolt} {seleccion.match.nombres}
            </strong>{' '}
            está en el padrón <strong>{seleccion.match.padronNombre}</strong>, que todavía está en
            borrador (sin cerrar). Cerrá el padrón para generar los legajos antes de hacer un alta
            manual.
          </p>
        </CartelBloqueo>
      )}

      {seleccion?.tipo === 'legajo' && (
        <DetalleAltaManual legajoId={seleccion.legajoId} onVolver={() => setSeleccion(null)} />
      )}
    </section>
  );
}

// ─────────────────────────── Buscador ───────────────────────────

function Buscador({ onElegir }: { onElegir: (m: BusquedaMatchItem) => void }) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');

  const q = useQuery({
    queryKey: ['alta-manual-busqueda', submitted],
    queryFn: () => buscarPersonas(submitted),
    enabled: submitted.length >= 2,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(query.trim());
  }

  return (
    <>
      <form onSubmit={handleSubmit} style={{ marginTop: 20, display: 'flex', gap: 8 }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            border: '1px solid var(--color-outline)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-background)',
          }}
        >
          <Search size={16} strokeWidth={2} style={{ opacity: 0.6 }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscá por DNI, CUIL, número de legajo o nombre/apellido"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              padding: '10px 0',
              background: 'transparent',
              fontSize: 14,
            }}
            autoFocus
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setSubmitted('');
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              title="Limpiar"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button type="submit" className="btn btn-primary" disabled={query.trim().length < 2}>
          Buscar
        </button>
      </form>

      <div style={{ marginTop: 20 }}>
        {q.isLoading && submitted && <TableSkeleton rows={5} />}
        {q.isError && (
          <p className="status status-error">No pudimos buscar. Reintentá en unos segundos.</p>
        )}
        {q.data && q.data.matches.length === 0 && (
          <p className="text-element-mid">Sin resultados para "{submitted}".</p>
        )}
        {q.data && q.data.matches.length > 0 && (
          <>
            <p className="text-element-mid" style={{ fontSize: 13 }}>
              {q.data.matches.length} {q.data.matches.length === 1 ? 'resultado' : 'resultados'}
              {q.data.truncado && ' (truncados, refiná la búsqueda)'} — elegí a la persona para
              continuar.
            </p>
            <div className="tableWrap" style={{ marginTop: 8 }}>
              <table className="dataTable">
                <thead>
                  <tr>
                    <th>DNI</th>
                    <th>Nombre</th>
                    <th>Parentesco</th>
                    <th>Titular</th>
                    <th>Legajo</th>
                    <th>Padrón</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.matches.map((m) => (
                    <tr
                      key={m.asociadoId}
                      onClick={() => onElegir(m)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ fontFamily: 'monospace' }}>{m.documento}</td>
                      <td>
                        {m.apellSolt} {m.nombres}
                      </td>
                      <td>{PARENTESCO_LABEL[m.parentesco] ?? m.parentesco}</td>
                      <td>{m.matchTipo === 'titular' ? '—' : m.titularNombre}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{m.legajoNumero}</td>
                      <td style={{ fontSize: 12 }}>{m.padronNombre}</td>
                      <td>
                        {m.legajoId ? (
                          <EstadoLegajoBadge estado={m.legajoEstado} />
                        ) : (
                          <span className="status status-warning" style={{ textTransform: 'none' }}>
                            Padrón borrador
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─────────────────────────── Detalle / preflight ───────────────────────────

function DetalleAltaManual({ legajoId, onVolver }: { legajoId: string; onVolver: () => void }) {
  const queryClient = useQueryClient();
  const queryKey = ['alta-manual-preflight', legajoId];

  const q = useQuery({
    queryKey,
    queryFn: () => getAltaManualPreflight(legajoId),
  });

  // Estado fuerza elegido a mano (solo si el titular no tiene DDJJ).
  const [titularidad, setTitularidad] = useState('');
  const [fuerza, setFuerza] = useState('');
  // Flags de progreso del flujo en 2 pasos.
  const [exportado, setExportado] = useState(false);
  const [altaHecha, setAltaHecha] = useState<{ numero: string } | null>(null);
  const [editando, setEditando] = useState<IntegranteAltaManual | null>(null);
  const [resolviendo, setResolviendo] = useState<ObservacionAltaManual | null>(null);

  const setPreflight = (pre: AltaManualPreflight) => queryClient.setQueryData(queryKey, pre);

  const exportMut = useMutation({
    mutationFn: () =>
      exportarAltaManual(
        legajoId,
        q.data?.requiereEstadoFuerza ? { titularidad, fuerza } : undefined,
      ),
    onSuccess: () => {
      setExportado(true);
      // El export queda registrado como alta "en curso" (pendiente del paso 2).
      queryClient.invalidateQueries({ queryKey: ['alta-manual-en-curso'] });
    },
  });

  const altaMut = useMutation({
    mutationFn: () => darDeAltaManual(legajoId),
    onSuccess: (r) => {
      setAltaHecha({ numero: r.numero });
      queryClient.invalidateQueries({ queryKey: ['alta-manual-en-curso'] });
    },
  });

  if (q.isLoading) {
    return (
      <div className="card" style={{ marginTop: 20 }}>
        <LinesSkeleton lines={6} />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <CartelBloqueo tono="error" titulo="No pudimos cargar el legajo" onVolver={onVolver}>
        <p style={{ margin: 0 }}>Reintentá en unos segundos.</p>
      </CartelBloqueo>
    );
  }

  const pre = q.data;

  // Pantalla final: alta hecha.
  if (altaHecha) {
    return (
      <div className="card" style={{ marginTop: 20, display: 'grid', gap: 14, placeItems: 'center', padding: 32 }}>
        <CheckCircle2 size={48} color="#15803d" />
        <h2 className="heading-4" style={{ margin: 0 }}>
          Legajo {altaHecha.numero} dado de alta
        </h2>
        <p className="text-element-mid" style={{ textAlign: 'center', maxWidth: 480, margin: 0 }}>
          Se marcó como <strong>dado de alta</strong> con el flag de alta manual. Recordá que el
          Excel ya fue cargado en Binary por vos en el paso anterior.
        </p>
        <button type="button" className="btn btn-primary" onClick={onVolver}>
          Hacer otra alta manual
        </button>
      </div>
    );
  }

  // Bloqueos duros: no se puede continuar.
  if (pre.bloqueos.yaEnAlta || pre.bloqueos.enBorrador || pre.bloqueos.esBis) {
    return (
      <CartelBloqueo
        tono="error"
        titulo={`No se puede dar de alta el legajo ${pre.legajo.numero}`}
        onVolver={onVolver}
      >
        {pre.bloqueos.yaEnAlta && (
          <p style={{ margin: '0 0 6px' }}>
            El legajo ya está en alta (estado <EstadoLegajoBadge estado={pre.legajo.estado} />). No
            corresponde un alta manual.
          </p>
        )}
        {pre.bloqueos.enBorrador && (
          <p style={{ margin: '0 0 6px' }}>
            El padrón del legajo está en <strong>borrador</strong>. Cerralo antes de dar el alta.
          </p>
        )}
        {pre.bloqueos.esBis && (
          <p style={{ margin: 0 }}>
            Son <strong>integrantes a sumar</strong> a un legajo que ya existe. Resolvelo desde la sección Alta Integrantes antes del alta.
          </p>
        )}
      </CartelBloqueo>
    );
  }

  const estadoFuerzaListo = !pre.requiereEstadoFuerza || (!!titularidad && !!fuerza);
  const motivos = motivosNoListo(pre, estadoFuerzaListo);
  const listo = motivos.length === 0;

  const observItem = resolviendo ? aObservacionItem(pre, resolviendo) : null;

  return (
    <div style={{ marginTop: 20, display: 'grid', gap: 16 }}>
      {/* Encabezado del legajo */}
      <div className="card" style={{ display: 'grid', gap: 10 }}>
        <div className="rowBetween" style={{ alignItems: 'baseline' }}>
          <div>
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              onClick={onVolver}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8 }}
            >
              <ArrowLeft size={14} /> Volver a la búsqueda
            </button>
            <h2 className="heading-5" style={{ margin: 0 }}>
              Legajo {pre.legajo.numero}{' '}
              <span style={{ marginLeft: 6 }}>
                <EstadoLegajoBadge estado={pre.legajo.estado} size={12} />
              </span>
            </h2>
            <p className="text-element-mid" style={{ fontSize: 12, marginTop: 4 }}>
              {pre.integrantes.length}{' '}
              {pre.integrantes.length === 1 ? 'integrante' : 'integrantes'} · padrón{' '}
              {pre.legajo.padronEstado || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Observaciones pendientes */}
      {pre.observacionesPendientes.length > 0 && (
        <div
          className="card"
          style={{ borderLeft: '4px solid var(--color-warning, #f59e0b)', display: 'grid', gap: 10 }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <AlertTriangle size={18} color="#b45309" />
            <strong>
              {pre.observacionesPendientes.length} observación
              {pre.observacionesPendientes.length === 1 ? '' : 'es'} sin resolver
            </strong>
          </div>
          <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
            Resolvé las observaciones reportadas por el titular antes de dar el alta.
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {pre.observacionesPendientes.map((o) => (
              <div
                key={o.obsIndex}
                className="rowBetween"
                style={{
                  alignItems: 'center',
                  gap: 8,
                  border: '1px solid var(--color-outline)',
                  borderRadius: 8,
                  padding: '8px 12px',
                }}
              >
                <div style={{ fontSize: 13 }}>
                  <strong>{o.campo}</strong> sobre {o.asociadoNombre || 'integrante'} (DNI{' '}
                  {o.asociadoDocumento || '—'})
                  {o.valorPropuesto && (
                    <span style={{ color: 'var(--color-element-mid)' }}>
                      {' '}
                      — propuesto "{o.valorPropuesto}"
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn--sm"
                  onClick={() => setResolviendo(o)}
                >
                  Resolver
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Integrantes */}
      <div className="card" style={{ display: 'grid', gap: 10 }}>
        <strong>Integrantes</strong>
        <div style={{ display: 'grid', gap: 8 }}>
          {pre.integrantes.map((i) => (
            <IntegranteCard key={i.asociadoId} integrante={i} onEditar={() => setEditando(i)} />
          ))}
        </div>
      </div>

      {/* Estado fuerza (si falta DDJJ) */}
      {pre.requiereEstadoFuerza ? (
        <div className="card" style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Info size={16} />
            <strong>Estado fuerza</strong>
          </div>
          <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
            El titular no tiene DDJJ cargada. Elegí la <strong>titularidad</strong> y la{' '}
            <strong>fuerza</strong> para completar la columna "estado fuerza" del Excel.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label className="formField" style={{ minWidth: 200 }}>
              <span>Titularidad</span>
              <CampoControl
                fieldKey="titularidad"
                value={titularidad}
                hasError={!titularidad}
                opciones={TITULARIDAD_OPCIONES}
                onChange={setTitularidad}
              />
            </label>
            <label className="formField" style={{ minWidth: 200 }}>
              <span>Fuerza</span>
              <CampoControl
                fieldKey="fuerza"
                value={fuerza}
                hasError={!fuerza}
                opciones={FUERZA_OPCIONES}
                onChange={setFuerza}
              />
            </label>
          </div>
        </div>
      ) : (
        <div className="card" style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <Info size={16} />
          <span>
            Estado fuerza (de la DDJJ del titular):{' '}
            <strong style={{ fontFamily: 'monospace' }}>{pre.estadoFuerza || '—'}</strong>
          </span>
        </div>
      )}

      {/* Resumen ¿Pasaría Binary? */}
      <div
        className="card"
        style={{
          borderLeft: `4px solid ${listo ? '#15803d' : 'var(--color-error, #dc2626)'}`,
          display: 'grid',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {listo ? (
            <>
              <ShieldCheck size={18} color="#15803d" />
              <strong style={{ color: '#15803d' }}>Los datos pasarían Binary</strong>
            </>
          ) : (
            <>
              <AlertTriangle size={18} color="#dc2626" />
              <strong style={{ color: '#b91c1c' }}>Todavía no se puede dar el alta</strong>
            </>
          )}
        </div>
        {!listo && (
          <ul style={{ margin: '0 0 0 4px', paddingLeft: 18, fontSize: 13 }}>
            {motivos.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Paso 1: descargar Excel */}
      <div className="card" style={{ display: 'grid', gap: 10 }}>
        <strong>Paso 1 — Descargar el Excel de alta</strong>
        <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
          Mismo formato que el alta masiva, para este único legajo. Se vuelve a validar todo en el
          servidor antes de generarlo.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!listo || exportMut.isPending}
            onClick={() => exportMut.mutate()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <Download size={16} />
            {exportMut.isPending ? 'Generando…' : 'Descargar Excel de alta'}
          </button>
          {exportado && !exportMut.isPending && (
            <span className="status status-success" style={{ textTransform: 'none' }}>
              ✓ Excel descargado
            </span>
          )}
        </div>
        {exportMut.isError && (
          <p className="status status-error" style={{ display: 'block' }}>
            {msgError(exportMut.error, 'No se pudo generar el Excel.')}
          </p>
        )}
      </div>

      {/* Paso 2: marcar dado de alta */}
      <div className="card" style={{ display: 'grid', gap: 10, opacity: exportado ? 1 : 0.6 }}>
        <strong>Paso 2 — Marcar como dado de alta</strong>
        <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
          Una vez que <strong>cargaste el Excel en Binary</strong>, marcá el legajo como dado de alta
          (queda registrado como alta manual). Este paso cambia el estado del legajo.
        </p>
        <div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!exportado || !listo || altaMut.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `¿Confirmás que ya cargaste el Excel del legajo ${pre.legajo.numero} en Binary y querés marcarlo como dado de alta?`,
                )
              ) {
                altaMut.mutate();
              }
            }}
          >
            {altaMut.isPending ? 'Marcando…' : 'Ya lo cargué en Binary → marcar dado de alta'}
          </button>
        </div>
        {altaMut.isError && (
          <p className="status status-error" style={{ display: 'block' }}>
            {msgError(altaMut.error, 'No se pudo marcar como dado de alta.')}
          </p>
        )}
      </div>

      {/* Modales */}
      {editando && (
        <EditarIntegranteModal
          legajoId={legajoId}
          legajoNumero={pre.legajo.numero}
          integrante={editando}
          onClose={() => setEditando(null)}
          onSaved={(nuevo) => {
            setPreflight(nuevo);
            setEditando(null);
          }}
        />
      )}

      {observItem && (
        <ResolverObservacionConRefetch
          legajoId={legajoId}
          item={observItem}
          onClose={() => setResolviendo(null)}
          onResuelta={() => {
            setResolviendo(null);
            queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────── Sub-componentes ───────────────────────────

function IntegranteCard({
  integrante: i,
  onEditar,
}: {
  integrante: IntegranteAltaManual;
  onEditar: () => void;
}) {
  const tieneError = i.camposConError.length > 0 || i.emailNoAscii;
  return (
    <article
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${tieneError ? 'var(--color-error, #dc2626)' : 'var(--color-outline)'}`,
        background: tieneError ? 'rgba(220,38,38,0.04)' : 'var(--color-background)',
      }}
    >
      <header className="rowBetween" style={{ alignItems: 'baseline', marginBottom: 6, gap: 8 }}>
        <strong style={{ fontSize: 14 }}>
          {i.apellSolt} {i.nombres}
          <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--color-element-mid)' }}>
            {i.esTitular ? 'Titular' : PARENTESCO_LABEL[i.parentesco] ?? i.parentesco}
          </span>
        </strong>
        <button
          type="button"
          className="btn btn-ghost btn--sm"
          onClick={onEditar}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Pencil size={13} /> Editar datos
        </button>
      </header>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 6,
          fontSize: 12,
        }}
      >
        <KV k="DNI" v={i.documento || '—'} mono />
        <KV k="CUIL" v={i.cuil || '—'} mono />
        <KV k="Sexo" v={i.sexo || '—'} />
        <KV k="Estado civil" v={i.estadoCivil || '—'} />
        <KV k="Fecha nac." v={i.fechaNacimiento || '—'} />
        <KV k="Email" v={i.email || '—'} />
      </div>

      {i.camposConError.length > 0 && (
        <p className="status status-error" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Datos faltantes o inválidos: {i.camposConError.join(', ')}
        </p>
      )}
      {i.emailNoAscii && (
        <p className="status status-error" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
          El email tiene caracteres inválidos (mojibake) — Binary lo rechaza. Corregilo.
        </p>
      )}
      {i.sexoCuilIncoherente && (
        <p
          className="status status-warning"
          style={{ display: 'block', marginTop: 6, fontSize: 12, textTransform: 'none' }}
        >
          Atención: el sexo no coincide con el prefijo del CUIL. Verificá que sea correcto.
        </p>
      )}
    </article>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--color-element-mid)' }}>{k}</div>
      <div style={{ fontFamily: mono ? 'monospace' : 'inherit' }}>{v}</div>
    </div>
  );
}

function CartelBloqueo({
  tono,
  titulo,
  onVolver,
  children,
}: {
  tono: 'error' | 'warning';
  titulo: string;
  onVolver: () => void;
  children: React.ReactNode;
}) {
  const color = tono === 'error' ? 'var(--color-error, #dc2626)' : 'var(--color-warning, #f59e0b)';
  return (
    <div className="card" style={{ marginTop: 20, borderLeft: `4px solid ${color}`, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <AlertTriangle size={18} color={color} />
        <strong>{titulo}</strong>
      </div>
      <div style={{ fontSize: 13 }}>{children}</div>
      <div>
        <button
          type="button"
          className="btn btn-ghost btn--sm"
          onClick={onVolver}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <ArrowLeft size={14} /> Volver a la búsqueda
        </button>
      </div>
    </div>
  );
}

/**
 * Editor de datos de UN integrante. Reusa CampoControl/FIELDS y valida de forma
 * intensa con `validarFilaAsociado` (mismo mirror que Legajos bis): marca y
 * bloquea no solo vacíos sino también valores inválidos. Manda al backend solo
 * el diff de campos editables; el backend revalida fila + cross-row.
 */
function EditarIntegranteModal({
  legajoId,
  legajoNumero,
  integrante,
  onClose,
  onSaved,
}: {
  legajoId: string;
  legajoNumero: string;
  integrante: IntegranteAltaManual;
  onClose: () => void;
  onSaved: (pre: AltaManualPreflight) => void;
}) {
  // 'legajo' no es editable (lo descarta el backend) pero el validador lo exige;
  // lo inyectamos con el número del legajo para que la validación pase.
  const original = useMemo<Record<string, string>>(() => {
    const o: Record<string, string> = { legajo: legajoNumero };
    for (const f of FIELDS) {
      if (f.key === 'legajo') continue;
      o[f.key as string] = (integrante as unknown as Record<string, string>)[f.key as string] ?? '';
    }
    return o;
  }, [integrante, legajoNumero]);

  const [form, setForm] = useState<Record<string, string>>(original);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const update = (k: string, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errores[k]) {
      setErrores((p) => {
        const n = { ...p };
        delete n[k];
        return n;
      });
    }
  };

  const mut = useMutation({
    mutationFn: (campos: Record<string, string>) =>
      editarIntegrantesAltaManual(legajoId, {
        integrantes: [{ asociadoId: integrante.asociadoId, campos }],
      }),
    onSuccess: (pre) => onSaved(pre),
  });

  const handleGuardar = () => {
    const errs = validarFilaAsociado(form);
    setErrores(errs);
    if (Object.keys(errs).length > 0) return;

    // Diff de campos editables (sin 'legajo').
    const diff: Record<string, string> = {};
    for (const f of FIELDS) {
      const k = f.key as string;
      if (k === 'legajo') continue;
      const nuevo = (form[k] ?? '').trim();
      if (nuevo !== (original[k] ?? '').trim()) diff[k] = nuevo;
    }
    if (Object.keys(diff).length === 0) {
      onClose();
      return;
    }
    mut.mutate(diff);
  };

  return (
    <div className="modalBackdrop" onClick={onClose}>
      <div className="modalCard" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <header className="modalHeader">
          <div>
            <h2 className="heading-5" style={{ margin: 0 }}>
              Editar datos — {integrante.apellSolt} {integrante.nombres}
            </h2>
            <p className="text-element-mid" style={{ marginTop: 6, fontSize: 13 }}>
              {integrante.esTitular ? 'Titular' : PARENTESCO_LABEL[integrante.parentesco] ?? integrante.parentesco}{' '}
              · legajo {legajoNumero}. Las validaciones se re-ejecutan al guardar.
            </p>
          </div>
          <button className="modalClose" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="modalGrid">
          {FIELDS.filter((f) => f.key !== 'legajo').map((f) => {
            const k = f.key as string;
            const vErr = errores[k];
            return (
              <label key={k} className="formField">
                <span>{f.label}</span>
                <CampoControl
                  fieldKey={k}
                  value={form[k] ?? ''}
                  placeholder={f.placeholder}
                  hasError={!!vErr}
                  opciones={CAMPO_OPCIONES[k]}
                  onChange={(v) => update(k, v)}
                />
                {vErr && (
                  <span
                    className="status status-error"
                    style={{ display: 'block', fontSize: 12, marginTop: 4 }}
                  >
                    {vErr}
                  </span>
                )}
              </label>
            );
          })}
        </div>

        {mut.isError && (
          <p className="status status-error" style={{ display: 'block', marginTop: 12 }}>
            {msgError(mut.error, 'No se pudieron guardar los cambios.')}
          </p>
        )}

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={mut.isPending}>
            <span className="btn-font-style">{mut.isPending ? 'Guardando…' : 'Guardar cambios'}</span>
          </button>
        </footer>
      </div>
    </div>
  );
}

/** Wrapper sobre ResolverObservacionModal que persiste y refetchea el preflight. */
function ResolverObservacionConRefetch({
  legajoId,
  item,
  onClose,
  onResuelta,
}: {
  legajoId: string;
  item: ObservacionItem;
  onClose: () => void;
  onResuelta: () => void;
}) {
  const mut = useMutation({
    // Variante alta-manual del resolver: autorizada por view:alta-manual (no
    // por rol) — el operador de altas puede ser USER_MEDICUS con override.
    mutationFn: (body: ResolverObservacionBody) =>
      resolverObservacionAltaManual(legajoId, item.obsIndex, body),
    onSuccess: onResuelta,
  });

  return (
    <ResolverObservacionModal
      item={item}
      submitting={mut.isPending}
      error={mut.isError ? msgError(mut.error, 'No se pudo resolver la observación.') : null}
      onClose={onClose}
      onSubmit={(body) => mut.mutate(body)}
    />
  );
}

// ─────────────────────────── Altas en curso ───────────────────────────

/**
 * Lista de altas manuales con el Excel exportado (paso 1) pero todavía sin
 * confirmar el `dado_de_alta` (paso 2) — lo que falta cerrar — más las
 * últimas confirmadas. Click en una fila retoma el flujo de ese legajo.
 */
function AltasEnCurso({ onAbrir }: { onAbrir: (legajoId: string) => void }) {
  const q = useQuery({
    queryKey: ['alta-manual-en-curso'],
    queryFn: getAltasManualesEnCurso,
    refetchInterval: 60_000,
  });

  if (q.isLoading || q.isError || !q.data) return null;
  const { pendientes, confirmadas } = q.data;
  if (pendientes.length === 0 && confirmadas.length === 0) return null;

  return (
    <div style={{ marginTop: 28, display: 'grid', gap: 16 }}>
      {pendientes.length > 0 && (
        <div className="card">
          <div className="rowBetween" style={{ alignItems: 'baseline' }}>
            <h2 className="heading-5" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={16} color="#B45309" />
              Pendientes de confirmar (paso 2)
            </h2>
            <span className="status status-warning" style={{ fontSize: 12 }}>
              {pendientes.length}
            </span>
          </div>
          <p className="text-element-mid" style={{ fontSize: 13, margin: '6px 0 0' }}>
            Excel descargado pero el legajo todavía no se marcó como dado de alta. Cuando Binary
            procese el archivo, entrá y confirmá el paso 2.
          </p>
          <TablaEnCurso items={pendientes} onAbrir={onAbrir} tipo="pendiente" />
        </div>
      )}

      {confirmadas.length > 0 && (
        <div className="card">
          <h2 className="heading-5" style={{ margin: 0 }}>
            Últimas confirmadas
          </h2>
          <TablaEnCurso items={confirmadas} onAbrir={onAbrir} tipo="confirmada" />
        </div>
      )}
    </div>
  );
}

function TablaEnCurso({
  items,
  onAbrir,
  tipo,
}: {
  items: AltaManualEnCursoItem[];
  onAbrir: (legajoId: string) => void;
  tipo: 'pendiente' | 'confirmada';
}) {
  const fmt = (s: string | null) => (s ? new Date(s).toLocaleString('es-AR') : '—');
  return (
    <div className="tableWrap" style={{ marginTop: 12 }}>
      <table className="dataTable">
        <thead>
          <tr>
            <th>Legajo</th>
            <th>Titular</th>
            <th>DNI</th>
            <th>Personas</th>
            <th>Estado</th>
            <th>{tipo === 'pendiente' ? 'Exportado' : 'Confirmado'}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.legajoId} onClick={() => onAbrir(i.legajoId)} style={{ cursor: 'pointer' }}>
              <td style={{ fontFamily: 'monospace' }}>
                <strong>{i.numero}</strong>
              </td>
              <td>
                {i.titularApellido} {i.titularNombre}
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{i.titularDocumento}</td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{i.cantidadPersonas}</td>
              <td>
                <EstadoLegajoBadge estado={i.estado} />
              </td>
              <td style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
                {tipo === 'pendiente' ? fmt(i.exportadoAt) : fmt(i.marcadoAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────── Helpers ───────────────────────────

/** Mapea la observación del preflight al shape que espera ResolverObservacionModal. */
function aObservacionItem(pre: AltaManualPreflight, o: ObservacionAltaManual): ObservacionItem {
  return {
    legajoId: pre.legajo.id,
    legajoNumero: pre.legajo.numero,
    padronId: pre.legajo.padronId,
    empresaId: pre.legajo.empresaId,
    obsIndex: o.obsIndex,
    campo: o.campo,
    esEnum: o.esEnum,
    valorActual: o.valorActual,
    valorPropuesto: o.valorPropuesto,
    mensaje: o.mensaje,
    reportadoAt: o.reportadoAt,
    asociadoId: o.asociadoId,
    asociadoNombre: o.asociadoNombre,
    asociadoDocumento: o.asociadoDocumento,
    asociadoParentesco: o.asociadoParentesco,
    esTitular: o.esTitular,
  };
}

/** Lista legible de por qué NO se puede dar el alta (vacía = listo). */
function motivosNoListo(pre: AltaManualPreflight, estadoFuerzaListo: boolean): string[] {
  const m: string[] = [];
  if (pre.observacionesPendientes.length > 0) {
    m.push(
      `${pre.observacionesPendientes.length} observación${
        pre.observacionesPendientes.length === 1 ? '' : 'es'
      } sin resolver`,
    );
  }
  const conError = pre.integrantes.filter((i) => i.camposConError.length > 0).length;
  if (conError > 0) {
    m.push(`${conError} integrante${conError === 1 ? '' : 's'} con datos faltantes o inválidos`);
  }
  if (pre.integrantes.some((i) => i.emailNoAscii)) {
    m.push('Hay un email con caracteres inválidos (mojibake)');
  }
  if (pre.crossRow.sinTitular) m.push('No hay exactamente un titular en el grupo');
  if (pre.crossRow.conyugeDuplicado) m.push('Hay más de un cónyuge en el grupo');
  if (pre.crossRow.cuilDuplicado) m.push('Hay CUIL duplicado entre integrantes');
  if (pre.crossRow.documentoDuplicado) m.push('Hay documento duplicado entre integrantes');
  if (!estadoFuerzaListo) m.push('Falta el estado fuerza: elegí titularidad y fuerza');
  return m;
}
